import React, { forwardRef } from "react"
import PropTypes from "prop-types"
import cn from "classnames"

import styles from "./popover.module.css"

export const BasePopover = forwardRef(
  (
    {
      children,
      open = false,
      size = "m",
      bezel = true,
      stretch = false,
      anchorStyles,
      inverse = false,
      ...props
    },
    ref
  ) => {
    // filtering out style and className
    /* eslint-disable-next-line react/prop-types */
    const { style, className, ...rest } = props

    const classes = cn(styles.Popover, styles[size], {
      [styles.bezelless]: !bezel,
      [styles.stretch]: stretch,
      [styles.inverse]: inverse,
    })

    // Render the dialog only when open is true to prevent floating-ui from repeatedly triggering resizeObserver.
    // https://github.com/floating-ui/floating-ui/issues/1740
    return open ? (
      <dialog
        {...rest}
        ref={ref}
        open={open}
        className={classes}
        style={anchorStyles}
      >
        {children}
      </dialog>
    ) : null
  }
)

BasePopover.displayName = "BasePopover"

BasePopover.propTypes = {
  /**
   * React node to render inside Base Popover.
   */
  children: PropTypes.node.isRequired,
  /**
   * Indicates the open state for Popover.
   */
  open: PropTypes.bool,
  /**
   * Size of the Popover.
   */
  size: PropTypes.oneOf(["s", "m", "l"]),
  /**
   * Adds padding on all sides.
   */
  bezel: PropTypes.bool,
  /**
   * Enables the Popover to stretch and fill the width of its container. By default, the Popover matches the width of its content.
   */
  stretch: PropTypes.bool,
  /**
   * Adds styles to position the Popover relative to the target element.
   */
  anchorStyles: PropTypes.object,
  /**
   * Set to true to render BasePopover with inverse background.
   */
  inverse: PropTypes.bool,
}

BasePopover.defaultProps = {
  open: false,
  size: "m",
  bezel: true,
  stretch: false,
}
